
import * as React from 'react';
import { useEffect } from 'react';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';

import {
  Delete as DeleteIcon,
  InfoOutlined as InfoIcon,
  AddCircle as AddCircleIcon
} from "@mui/icons-material";

import { FieldArray, useFormikContext } from 'formik';

import { InputField, DatePickerField } from '../../FormFields';
import Grid from '@mui/material/Grid';



export default function ProjectPhase(props) {
  const { setFieldValue } = useFormikContext();

  const {
    formField: {
      description,
      projectDetails,
    }, values } = props;
  const isProjectStudy = typeof values?.isProjectStudy === 'boolean'
    ? values.isProjectStudy
    : String(values?.isProjectStudy || '').toLowerCase() === 'yes';

  useEffect(() => {
    // initialize projectDetails with one empty row if null or empty
    if (!values?.projectDetails || values.projectDetails.length === 0) {
      setFieldValue('projectDetails', [{
        phase: 1,
        csjNumber: '',
        letDate: '',
        completionDate: '',
        costEstimate: '',
        ...(isProjectStudy ? { studyCost: '' } : {}),
      }], false);
      return;
    }

    // add/remove studyCost field based on project study selection
    const hasStudyCost = values.projectDetails.some((phase) => Object.hasOwn(phase, 'studyCost'));

    if (isProjectStudy && !hasStudyCost) {
      const projectDetailsWithStudyCost = values.projectDetails.map((phase) => ({ ...phase, studyCost: '' }));
      setFieldValue('projectDetails', projectDetailsWithStudyCost, false);
      return;
    }

    if (!isProjectStudy && hasStudyCost) {
      const projectDetailsWithoutStudyCost = values.projectDetails.map(({ studyCost, ...rest }) => rest);
      setFieldValue('projectDetails', projectDetailsWithoutStudyCost, false);
    }
  }, [isProjectStudy, values?.projectDetails, setFieldValue]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sx={{ marginRight: "10px" }}>
        <InputField name={description.name} label={description.label} fullWidth />
      </Grid>
      <Grid item xs={12} sx={{ marginRight: "10px" }}>

        <FieldArray name="projectDetails">
          {({ remove, push }) => (
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              <TableContainer >

                <Table stickyHeader aria-label="Project phases table">
                  <TableHead>
                    <TableRow>
                      <TableCell align={'center'} style={{ minWidth: 80, fontWeight: 'bold' }} > Actions </TableCell>
                      <TableCell align={'center'} style={{ fontWeight: 'bold' }}> {projectDetails.phaseNumber.label} </TableCell>
                      <TableCell align={'center'} style={{ minWidth: 170, fontWeight: 'bold' }} > {projectDetails.csjNumber.label} </TableCell>
                      <TableCell align={'center'} style={{ minWidth: 170, fontWeight: 'bold' }} > {projectDetails.letDate.label(isProjectStudy)} </TableCell>
                      <TableCell align={'center'} style={{ minWidth: 170, fontWeight: 'bold' }} > {projectDetails.completionDate.label} </TableCell>
                      {isProjectStudy &&
                        <TableCell align={'center'} style={{ minWidth: 170, fontWeight: 'bold' }} > {projectDetails.studyCost.label} </TableCell>
                      }
                      <TableCell align={'center'} style={{ minWidth: 170, fontWeight: 'bold' }} > {projectDetails.costEstimate.label} {!isProjectStudy && <span>*</span>} </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      values.projectDetails.length > 0 &&
                      values.projectDetails.map((project, index) =>
                      (
                        <TableRow key={index}>

                          <TableCell align={'center'} style={{ minWidth: 80, fontWeight: 'bold' }} >
                            <Tooltip title="Add a new row/phase" placement='left'>
                              <IconButton
                                type="button"
                                aria-label="Add new phase"
                                onClick={() => push({
                                  phase: values.projectDetails.length + 1,
                                  csjNumber: '',
                                  letDate: '',
                                  completionDate: '',
                                  costEstimate: '',
                                  ...(isProjectStudy ? { studyCost: '' } : {}),
                                })}
                              > <AddCircleIcon />
                              </IconButton>
                            </Tooltip>
                            {/* Disable the delete action if the project phase details are less than 1 */}
                            {values.projectDetails.length > 1 &&
                              <Tooltip title="Delete row/phase" placement='left'>
                                <IconButton type="button" aria-label="Delete phase" onClick={() => remove(index)}> <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            }
                          </TableCell>

                          <TableCell align={'center'}>
                            <Typography>{index + 1}</Typography>
                          </TableCell>
                          <TableCell align={'center'}>
                            <InputField
                              key={`gridVal${index}csjNumber`}
                              name={`projectDetails.${index}.csjNumber`}
                              inputProps={{ 'aria-label': `${projectDetails.csjNumber.label} for phase ${index + 1}` }}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell align={'center'}>
                            <DatePickerField
                              key={`gridVal${index}letDate`}
                              name={`projectDetails.${index}.letDate`}
                              textFieldProps={{ inputProps: { 'aria-label': `${projectDetails.letDate.label(isProjectStudy)} for phase ${index + 1}` } }}
                              fullWidth
                            />
                          </TableCell>

                          <TableCell align={'center'}>
                            <DatePickerField
                              key={`gridVal${index}completionDate`}
                              name={`projectDetails.${index}.completionDate`}
                              textFieldProps={{ inputProps: { 'aria-label': `${projectDetails.completionDate.label} for phase ${index + 1}` } }}
                              fullWidth
                            />
                          </TableCell>
                          {isProjectStudy &&
                            <TableCell align={'center'} >
                              <InputField
                                key={`gridVal${index}studyCost`}
                                name={`projectDetails.${index}.studyCost`}
                                inputProps={{ 'aria-label': `${projectDetails.studyCost.label} for phase ${index + 1}` }}
                                fullWidth
                              />
                            </TableCell>
                          }
                          <TableCell align={'center'}>
                            <InputField
                              key={`gridVal${index}costEstimate`}
                              name={`projectDetails.${index}.costEstimate`}
                              inputProps={{ 'aria-label': `${projectDetails.costEstimate.label} for phase ${index + 1}` }}
                              fullWidth
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </FieldArray>
      </Grid>
    </Grid >
  );
}